import React from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  GlobeAltIcon,
  TrophyIcon,
  HeartIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const AboutPage = () => {
  const milestones = [
    {
      year: '2010',
      title: 'Kuruluş',
              description: 'FielHome olarak lüks mobilya sektöründe yolculuğumuza başladık',
    },
    {
      year: '2015',
      title: 'İlk Showroom',
              description: 'İnegöl\'de ilk flagship mağazamızı açtık',
    },
    {
      year: '2018',
      title: 'Uluslararası Genişleme',
      description: 'Avrupa pazarına giriş yaptık ve 5 ülkede hizmet vermeye başladık',
    },
    {
      year: '2021',
      title: 'AI Teknolojisi',
      description: '3D görselleştirme ve AI destekli tasarım sistemimizi geliştirdik',
    },
    {
      year: '2024',
      title: 'Pazar Lideri',
      description: 'Türkiye\'nin en büyük premium mobilya markası olduk',
    },
  ];

  const values = [
    {
      icon: SparklesIcon,
      title: 'Kalite',
      description: 'Her ürünümüzde en yüksek kalite standartlarını uygularız',
    },
    {
      icon: HeartIcon,
      title: 'Müşteri Memnuniyeti',
      description: 'Müşterilerimizin memnuniyeti bizim önceliğimizdir',
    },
    {
      icon: GlobeAltIcon,
      title: 'Sürdürülebilirlik',
      description: 'Çevre dostu üretim yöntemleri kullanırız',
    },
    {
      icon: TrophyIcon,
      title: 'İnovasyon',
      description: 'Sürekli yenilik ve teknoloji odaklı yaklaşım benimseriz',
    },
  ];

  const stats = [
    { number: '50,000+', label: 'Mutlu Müşteri' },
    { number: '15,000+', label: 'Ürün Çeşidi' },
    { number: '25+', label: 'Şehir' },
    { number: '14', label: 'Yıllık Deneyim' },
  ];

  const team = [
    {
      name: 'Ahmet Demir',
      role: 'Genel Müdür',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
      description: '20 yıllık mobilya sektörü deneyimi',
    },
    {
      name: 'Elif Kaya',
      role: 'Tasarım Direktörü',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face',
      description: 'Uluslararası tasarım ödülleri sahibi',
    },
    {
      name: 'Mehmet Öz',
      role: 'Teknoloji Direktörü',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&crop=face',
      description: 'AI ve 3D teknolojileri uzmanı',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-black/30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <BuildingOfficeIcon className="h-8 w-8 text-white" />
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-luxury font-bold text-white mb-6">
            Hakkımızda
          </h1>
            
            <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
              14 yıldır, evlerinizi hayallerinizdeki yaşam alanlarına dönüştürüyoruz. 
              Kalite, estetik ve teknolojinin mükemmel uyumu.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-gray-900 mb-6">
                Misyonumuz
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Türkiye\'nin önde gelen premium mobilya markası olarak, müşterilerimize 
                sadece mobilya değil, yaşam tarzı sunuyoruz. Her ürünümüz, konfor, 
                estetik ve dayanıklılığın mükemmel dengesi olacak şekilde tasarlanır.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Yapay zeka destekli 3D görselleştirme teknolojimiz sayesinde, 
                müşterilerimiz ürünleri satın almadan önce kendi evlerinde nasıl 
                görüneceğini deneyimleyebilir.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-slate-600 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop&crop=center"
                alt="Modern Office"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-slate-600/20 rounded-full blur-3xl" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-slate-400/20 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-gray-900 mb-6">
              Değerlerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              İşimizin merkezinde yer alan temel değerler, her kararımızda bizi yönlendirir
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-6">
                  <value.icon className="h-8 w-8 text-slate-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-gray-900 mb-6">
              Tarihçemiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              14 yıllık yolculuğumuzda kat ettiğimiz önemli kilometre taşları
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-slate-200" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`flex items-center ${
                    index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                  }`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                      <div className="text-2xl font-bold text-slate-600 mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="relative z-10">
                    <div className="w-4 h-4 bg-slate-600 rounded-full border-4 border-white shadow-lg" />
                  </div>
                  
                  <div className="w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-gray-900 mb-6">
              Yönetim Ekibi
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alanında uzman, deneyimli ekibimizle sizlere en iyi hizmeti sunuyoruz
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-slate-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600">
                    {member.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl lg:text-4xl font-luxury font-bold text-white mb-6">
              Bizimle İletişime Geçin
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Sorularınız için buradayız. Uzman ekibimiz size en iyi çözümü sunmak için hazır.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 bg-white text-slate-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300"
              >
                İletişime Geç
              </a>
              <a
                href="/products"
                className="inline-flex items-center px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-colors duration-300"
              >
                Ürünleri İncele
              </a>
            </div>
          </motion.div>
      </div>
      </section>
    </div>
  );
};

export default AboutPage; 